// 日志工具
// 提供统一的日志记录功能

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: unknown;
  context?: string;
  stack?: string;
}

export interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  enableStorage: boolean;
  maxStorageEntries: number;
  storageKey: string;
  format: 'json' | 'text';
}

const DEFAULT_CONFIG: LoggerConfig = {
  minLevel: 'info',
  enableConsole: true,
  enableStorage: false,
  maxStorageEntries: 1000,
  storageKey: 'html_ppt_logs',
  format: 'text',
};

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * 日志记录器类
 */
export class Logger {
  private config: LoggerConfig;
  private logs: LogEntry[] = [];
  private quotaErrorNotified = false;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.loadLogs();
  }

  /**
   * 记录调试日志
   */
  debug(message: string, data?: unknown, context?: string): void {
    this.log('debug', message, data, context);
  }

  /**
   * 记录信息日志
   */
  info(message: string, data?: unknown, context?: string): void {
    this.log('info', message, data, context);
  }

  /**
   * 记录警告日志
   */
  warn(message: string, data?: unknown, context?: string): void {
    this.log('warn', message, data, context);
  }

  /**
   * 记录错误日志
   */
  error(message: string, error?: Error | unknown, context?: string): void {
    const data = error instanceof Error ? {
      message: error.message,
      stack: error.stack,
      name: error.name,
    } : error;

    this.log('error', message, data, context);
  }

  /**
   * 记录工作流事件
   */
  workflowEvent(event: string, data?: unknown, context?: string): void {
    this.info(`[Workflow] ${event}`, data, context);
  }

  /**
   * 记录API调用
   */
  apiCall(method: string, url: string, data?: unknown, context?: string): void {
    this.debug(`[API] ${method} ${url}`, data, context);
  }

  /**
   * 记录API响应
   */
  apiResponse(method: string, url: string, status: number, data?: unknown, context?: string): void {
    const level = status >= 400 ? 'error' : 'info';
    this.log(level, `[API] ${method} ${url} - ${status}`, data, context);
  }

  /**
   * 记录文件操作
   */
  fileOperation(operation: string, filename: string, data?: unknown, context?: string): void {
    this.info(`[File] ${operation}: ${filename}`, data, context);
  }

  /**
   * 获取所有日志
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * 获取指定级别的日志
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  /**
   * 获取指定上下文的日志
   */
  getLogsByContext(context: string): LogEntry[] {
    return this.logs.filter(log => log.context === context);
  }

  /**
   * 清空日志
   */
  clear(): void {
    this.logs = [];
    if (this.config.enableStorage) {
      localStorage.removeItem(this.config.storageKey);
    }
  }

  /**
   * 导出日志
   */
  export(format: 'json' | 'text' = 'json'): string {
    if (format === 'json') {
      return JSON.stringify(this.logs, null, 2);
    } else {
      return this.logs.map(log => this.formatLogEntry(log)).join('\n');
    }
  }

  /**
   * 内部日志记录方法
   */
  private log(level: LogLevel, message: string, data?: unknown, context?: string): void {
    if (LEVEL_PRIORITY[level] < LEVEL_PRIORITY[this.config.minLevel]) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      context,
    };

    // 如果是错误级别，添加堆栈信息
    if (level === 'error' && typeof data === 'object' && data !== null && 'stack' in data) {
      const stack = (data as { stack?: unknown }).stack;
      if (typeof stack === 'string') {
        entry.stack = stack;
      }
    }

    this.logs.push(entry);

    // 控制台输出
    if (this.config.enableConsole) {
      this.consoleLog(entry);
    }

    // 存储日志
    if (this.config.enableStorage) {
      this.saveLogs();
    }

    // 限制日志数量
    if (this.logs.length > this.config.maxStorageEntries) {
      this.logs = this.logs.slice(-this.config.maxStorageEntries);
    }
  }

  /**
   * 控制台输出
   */
  private consoleLog(entry: LogEntry): void {
    const timestamp = new Date(entry.timestamp).toLocaleTimeString();
    const prefix = `[${timestamp}] [${entry.level.toUpperCase()}]`;
    const context = entry.context ? `[${entry.context}]` : '';
    const message = `${prefix}${context} ${entry.message}`;

    switch (entry.level) {
      case 'debug':
        console.debug(message, entry.data || '');
        break;
      case 'info':
        console.info(message, entry.data || '');
        break;
      case 'warn':
        console.warn(message, entry.data || '');
        break;
      case 'error':
        console.error(message, entry.data || '');
        if (entry.stack) {
          console.error('Stack:', entry.stack);
        }
        break;
    }
  }

  /**
   * 格式化日志条目
   */
  private formatLogEntry(entry: LogEntry): string {
    const timestamp = new Date(entry.timestamp).toLocaleString();
    const context = entry.context ? ` [${entry.context}]` : '';
    const data = entry.data ? `\n  Data: ${JSON.stringify(entry.data, null, 2)}` : '';
    const stack = entry.stack ? `\n  Stack: ${entry.stack}` : '';

    return `[${timestamp}] [${entry.level.toUpperCase()}]${context} ${entry.message}${data}${stack}`;
  }

  /**
   * 保存日志到本地存储
   */
  private saveLogs(): void {
    try {
      const logsToSave = this.logs.slice(-this.config.maxStorageEntries);
      localStorage.setItem(this.config.storageKey, JSON.stringify(logsToSave));
    } catch (error) {
      const err = error as { name?: string; message?: string };
      const message = err?.message || '';
      if (err?.name === 'QuotaExceededError' || message.includes('exceeded the quota')) {
        this.logs = this.logs.slice(-Math.max(50, Math.floor(this.config.maxStorageEntries / 2)));
        try {
          localStorage.setItem(this.config.storageKey, JSON.stringify(this.logs));
          return;
        } catch (e) { void e }
        this.config.enableStorage = false;
        if (!this.quotaErrorNotified) {
          this.quotaErrorNotified = true;
        }
        return;
      }
      console.error('Failed to save logs:', error);
    }
  }

  /**
   * 从本地存储加载日志
   */
  private loadLogs(): void {
    if (!this.config.enableStorage) {
      return;
    }

    try {
      const savedLogs = localStorage.getItem(this.config.storageKey);
      if (savedLogs) {
        const parsedLogs = JSON.parse(savedLogs);
        if (Array.isArray(parsedLogs)) {
          this.logs = parsedLogs;
        }
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  }
}

// 全局日志实例
export const logger = new Logger({
  minLevel: (import.meta as { env?: { DEV?: boolean } }).env?.DEV ? 'debug' : 'info',
  enableStorage: Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV),
  maxStorageEntries: 200,
});

// 快捷方法
export const log = {
  debug: (message: string, data?: unknown, context?: string) => logger.debug(message, data, context),
  info: (message: string, data?: unknown, context?: string) => logger.info(message, data, context),
  warn: (message: string, data?: unknown, context?: string) => logger.warn(message, data, context),
  error: (message: string, error?: unknown, context?: string) => logger.error(message, error, context),
  workflow: (event: string, data?: unknown, context?: string) => logger.workflowEvent(event, data, context),
  api: {
    call: (method: string, url: string, data?: unknown, context?: string) => logger.apiCall(method, url, data, context),
    response: (method: string, url: string, status: number, data?: unknown, context?: string) => logger.apiResponse(method, url, status, data, context),
  },
  file: (operation: string, filename: string, data?: unknown, context?: string) => logger.fileOperation(operation, filename, data, context),
};
